import { paymentMessages } from "../common/payment-messages";
import { AccountNumber, Luhn, LuhnGeneral, ValidateBicAndAccount } from "./rules";

/**
 * Appends payment validation rules to an existing ValidationRules chain.
 *
 * @param {object} chain - A ValidationRules chain (the return value of ValidationRules.ensure() or .withMessage() etc.)
 * @param {object} options
 * @param {string[]} options.methods - Payment methods to validate, e.g. ['Bankgiro', 'Plusgiro', 'Bankkonto', 'IBAN']
 * @param {string} [options.lang='sv'] - Language for error messages ('sv' or 'en')
 * @param {string} [options.format='standard'] - Format mode: 'standard' (digits only), 'hyphenated' (strict hyphen format), 'relaxed' (allows spaces/hyphens)
 * @param {boolean} [options.showBankName=true] - Whether bank name field is validated for Bankkonto
 * @param {boolean} [options.showClearingCode=true] - Whether clearing code field is validated for Bankkonto
 * @param {boolean} [options.showHolderName=false] - Whether account holder name is validated
 * @param {boolean} [options.showReference=false] - Whether payment reference is validated
 * @param {boolean} [options.validateIbanWithBic=false] - Use validateBicAndAccount rule (cross-validates BIC+IBAN via iban npm package)
 * @returns {object} The chain with payment rules appended
 */
export function addPaymentValidation(chain, options = {}) {
  const {
    methods = [],
    lang = "sv",
    format = "standard",
    showBankName = true,
    showClearingCode = true,
    showHolderName = false,
    showReference = false,
    validateIbanWithBic = false,
  } = options;

  const msg = paymentMessages[lang]?.validation ?? paymentMessages.sv.validation;

  // Payment method required
  chain = chain
    .ensure((p) => p.paymentMethod)
    .required()
    .withMessage(msg.methodRequired);

  // Bankgiro
  if (methods.includes("Bankgiro")) {
    chain = addBankgiroValidation(chain, msg, format);
  }

  // Plusgiro
  if (methods.includes("Plusgiro")) {
    chain = addPlusgiroValidation(chain, msg, format);
  }

  // Bankkonto
  if (methods.includes("Bankkonto")) {
    chain = addBankkontoValidation(chain, msg, { showBankName, showClearingCode });
  }

  // Personkonto (same clearing validation as Bankkonto but no bank name)
  if (methods.includes("Personkonto")) {
    chain = addPersonkontoValidation(chain, msg);
  }

  // IBAN
  if (methods.includes("IBAN")) {
    chain = addIbanValidation(chain, msg, { validateIbanWithBic });
  }

  // Annan (Other)
  if (methods.includes("Annan")) {
    chain = chain
      .ensure((p) => p.paymentInformation)
      .required()
      .when((o) => o.paymentMethod === "Annan")
      .withMessage(msg.annanRequired);
  }

  // Account holder name
  if (showHolderName) {
    chain = chain
      .ensure((p) => p.paymentBankAccountHolderName)
      .required()
      .withMessage(msg.holderNameRequired)
      .matches(/^[0-9a-zA-ZåäöÅÄÖ\s-]+$/)
      .when(
        (p) =>
          p.paymentMethod === "Bankkonto" ||
          p.paymentMethod === "Bankgiro" ||
          p.paymentMethod === "Plusgiro"
      )
      .withMessage(msg.holderNameFormat);
  }

  // Payment reference
  if (showReference) {
    chain = chain
      .ensure((p) => p.paymentReference)
      .matches(/^[1234567890a-zA-Z\s\-/()?:.,+]+$/)
      .withMessage(msg.referenceFormat);
  }

  return chain;
}

function addBankgiroValidation(chain, msg, format) {
  chain = chain
    .ensure((p) => p.paymentInformation)
    .required()
    .when((o) => o.paymentMethod === "Bankgiro")
    .withMessage(msg.bankgiroRequired);

  if (format === "hyphenated") {
    chain = chain
      .matches(/^\d{3,4}-\d{4,4}$/)
      .when((o) => o.paymentMethod === "Bankgiro" && !!o.paymentInformation)
      .withMessage(msg.bankgiroFormat)
      .satisfiesRule(new Luhn())
      .when((o) => o.paymentMethod === "Bankgiro" && !!o.paymentInformation)
      .withMessage(msg.bankgiroInvalid);
  } else if (format === "relaxed") {
    chain = chain
      .matches(/^[0-9 ]+(-?[0-9 ]+)+$/)
      .when((o) => o.paymentMethod === "Bankgiro" && !!o.paymentInformation)
      .withMessage(msg.bankgiroDigits)
      .satisfiesRule(new Luhn())
      .when((o) => o.paymentMethod === "Bankgiro" && !!o.paymentInformation)
      .withMessage(msg.bankgiroInvalid);
  } else {
    // standard: 7-8 digits, optional hyphen in correct position (XXX-XXXX or XXXX-XXXX), optional spaces
    chain = chain
      .matches(/^[\d ]{3,4}-?[\d ]{4}$/)
      .when((o) => o.paymentMethod === "Bankgiro" && !!o.paymentInformation)
      .withMessage(msg.bankgiroFormat)
      .satisfies((value) => {
        const digits = (value || "").replace(/[\s-]/g, "");
        return digits.length >= 7 && digits.length <= 8;
      })
      .when((o) => o.paymentMethod === "Bankgiro" && !!o.paymentInformation)
      .withMessage(msg.bankgiroMin)
      .satisfiesRule(new Luhn())
      .when((o) => o.paymentMethod === "Bankgiro" && !!o.paymentInformation)
      .withMessage(msg.bankgiroInvalid);
  }

  return chain;
}

function addPlusgiroValidation(chain, msg, format) {
  chain = chain
    .ensure((p) => p.paymentInformation)
    .required()
    .when((o) => o.paymentMethod === "Plusgiro")
    .withMessage(msg.plusgiroRequired);

  if (format === "hyphenated") {
    chain = chain
      .matches(/^\d{5,7}-\d{1}$/)
      .when((o) => o.paymentMethod === "Plusgiro" && !!o.paymentInformation)
      .withMessage(msg.plusgiroFormat)
      .satisfiesRule(new LuhnGeneral())
      .when((o) => o.paymentMethod === "Plusgiro" && !!o.paymentInformation)
      .withMessage(msg.plusgiroInvalid);
  } else if (format === "relaxed") {
    chain = chain
      .matches(/^[0-9 ]+(-?[0-9 ]+)+$/)
      .when((o) => o.paymentMethod === "Plusgiro" && !!o.paymentInformation)
      .withMessage(msg.plusgiroDigits)
      .satisfiesRule(new LuhnGeneral())
      .when((o) => o.paymentMethod === "Plusgiro" && !!o.paymentInformation)
      .withMessage(msg.plusgiroInvalid);
  } else {
    // standard: 2-8 digits, optional hyphen before last digit (XXXXXXX-X), optional spaces
    chain = chain
      .matches(/^(\d ?){1,7}-?\d$/)
      .when((o) => o.paymentMethod === "Plusgiro" && !!o.paymentInformation)
      .withMessage(msg.plusgiroFormat)
      .satisfies((value) => {
        const digits = (value || "").replace(/[\s-]/g, "");
        return digits.length >= 2 && digits.length <= 8;
      })
      .when((o) => o.paymentMethod === "Plusgiro" && !!o.paymentInformation)
      .withMessage(msg.plusgiroMin)
      .satisfiesRule(new LuhnGeneral())
      .when((o) => o.paymentMethod === "Plusgiro" && !!o.paymentInformation)
      .withMessage(msg.plusgiroInvalid);
  }

  return chain;
}

function addBankkontoValidation(chain, msg, { showBankName, showClearingCode }) {
  if (showBankName) {
    chain = chain
      .ensure((p) => p.paymentDescription)
      .required()
      .when((o) => o.paymentMethod === "Bankkonto")
      .withMessage(msg.bankNameRequired);
  }

  if (showClearingCode) {
    chain = chain
      .ensure((p) => p.paymentBankCode)
      .required()
      .when((o) => o.paymentMethod === "Bankkonto")
      .withMessage(msg.clearingRequired)
      .matches(/^[0-9]+$/)
      .when((o) => o.paymentMethod === "Bankkonto" && !!o.paymentBankCode)
      .withMessage(msg.clearingDigits)
      .satisfies((v) => {
        const len = (v ?? "").length;
        return len === 4 || len === 5;
      })
      .when((o) => o.paymentMethod === "Bankkonto" && !!o.paymentBankCode)
      .withMessage(msg.clearingLength);
  }

  chain = chain
    .ensure((p) => p.paymentInformation)
    .required()
    .when((o) => o.paymentMethod === "Bankkonto")
    .withMessage(msg.bankAccountRequired)
    .matches(/^[0-9]+$/)
    .when((o) => o.paymentMethod === "Bankkonto" && !!o.paymentInformation)
    .withMessage(msg.bankAccountDigits)
    .satisfiesRule(new AccountNumber("paymentBankCode"))
    .when((o) => o.paymentMethod === "Bankkonto" && !!o.paymentInformation)
    .withMessage(msg.bankAccountInvalid);

  return chain;
}

function addPersonkontoValidation(chain, msg) {
  chain = chain
    .ensure((p) => p.paymentBankCode)
    .required()
    .when((o) => o.paymentMethod === "Personkonto")
    .withMessage(msg.clearingRequired);

  chain = chain
    .ensure((p) => p.paymentInformation)
    .required()
    .when((o) => o.paymentMethod === "Personkonto")
    .withMessage(msg.bankAccountRequired)
    .matches(/^[0-9]+$/)
    .when((o) => o.paymentMethod === "Personkonto" && !!o.paymentInformation)
    .withMessage(msg.bankAccountDigits)
    .satisfiesRule(new AccountNumber("paymentBankCode"))
    .when((o) => o.paymentMethod === "Personkonto" && !!o.paymentInformation)
    .withMessage(msg.bankAccountInvalid);

  return chain;
}

function addIbanValidation(chain, msg, { validateIbanWithBic }) {
  chain = chain
    .ensure((p) => p.paymentInformation)
    .required()
    .when((o) => o.paymentMethod === "IBAN")
    .withMessage(msg.ibanRequired);

  if (validateIbanWithBic) {
    // Use the validateBicAndAccount custom rule that cross-validates via iban npm package
    chain = chain
      .satisfiesRule(new ValidateBicAndAccount("paymentBankCode"))
      .when((p) => p.paymentMethod === "IBAN" && !!p.paymentInformation && !!p.paymentBankCode)
      .withMessage(msg.ibanInvalid);
  } else {
    // Manual IBAN format validation
    chain = chain
      .matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/)
      .when((o) => o.paymentMethod === "IBAN" && !!o.paymentInformation)
      .withMessage(msg.ibanFormat)
      .minLength(15)
      .when((o) => o.paymentMethod === "IBAN" && !!o.paymentInformation)
      .withMessage(msg.ibanMin)
      .maxLength(34)
      .when((o) => o.paymentMethod === "IBAN" && !!o.paymentInformation)
      .withMessage(msg.ibanMax);
  }

  // BIC validation (always needed for IBAN)
  chain = chain
    .ensure((p) => p.paymentBankCode)
    .required()
    .when((o) => o.paymentMethod === "IBAN")
    .withMessage(msg.bicRequired)
    .satisfies((value) => !!value && (value.length === 8 || value.length === 11))
    .when((o) => o.paymentMethod === "IBAN" && !!o.paymentBankCode)
    .withMessage(msg.bicLength)
    .matches(/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/)
    .when(
      (o) =>
        o.paymentMethod === "IBAN" &&
        o.paymentBankCode &&
        (o.paymentBankCode.length === 8 || o.paymentBankCode.length === 11)
    )
    .withMessage(msg.bicFormat);

  return chain;
}
